import React, { useEffect, createContext, useState } from 'react';
import { PAGE_VIEW, UpdatingChatHistoryState } from '../types';
export const INITIAL_CHATBOT_MESSAGE =
  'Welcome to Generative Market Analysis! Please type in your question below or select one of the starter questions.';

type Session = {
  pageView: PAGE_VIEW;
  chatSessionId: string;
  setChatSessionId: (id: string) => void;
  setPageView: (pageView: PAGE_VIEW) => void;
  accountDetails: any;
  setAccountDetails: (details: any) => void;
  selectedSuggestion: string;
  setSelectedSuggestion: (suggestionId: string) => void;
  openAI: boolean;
  setOpenAI: (flag: any) => void;
  genAI: boolean;
  setGenAI: (flag: any) => void;
  isPromptRetried: boolean;
  setIsPromptRetried: (flag: any) => void;
  isPromptGenerating: boolean;
  setIsPromptGenerating: (flag: boolean) => void;
  downloadChatHistory: string;
  setDownloadChatHistory: (history: string) => void;
  promptRetriedSource: string;
  setPromptRetriedSource: (retriedSource: string) => void;
  isUpdatingChatHistory: UpdatingChatHistoryState;
  setIsUpdatingChatHistory: (isUpdatingChatHistory: any) => void;
  deviceType: string;
};

const defaultSession = {
  pageView: PAGE_VIEW.LANDING,
  setPageView: (pageView: PAGE_VIEW) => undefined,
  chatSessionId: '',
  setChatSessionId: (chatSessionId: string) => undefined,
  accountDetails: {},
  setAccountDetails: (details: any) => undefined,
  selectedSuggestion: '',
  setSelectedSuggestion: (suggestionId: string) => undefined,
  openAI: false,
  setOpenAI: (flag: any) => undefined,
  genAI: false,
  setGenAI: (flag: any) => undefined,
  isPromptRetried: false,
  setIsPromptRetried: (flag: any) => undefined,
  isPromptGenerating: false,
  setIsPromptGenerating: (flag: boolean) => undefined,
  downloadChatHistory: `AI: ${INITIAL_CHATBOT_MESSAGE}\n`,
  setDownloadChatHistory: (history: string) => undefined,
  promptRetriedSource: '',
  setPromptRetriedSource: (promptRetriedSource: string) => undefined,
  isUpdatingChatHistory: { state: false, action: '' },
  setIsUpdatingChatHistory: (isUpdatingChatHistory: any) => undefined,
  deviceType: '',
};
export const SessionContext = createContext<Session>(defaultSession);

export const SessionProvider = (props: any) => {
  const { resetApp, setResetApp } = props;
  const [pageView, setPageView] = useState(PAGE_VIEW.LANDING);
  const [chatSessionId, setChatSessionId] = useState('');
  const [accountDetails, setAccountDetails] = useState({});
  //store selectedPrompt/Starter question as string
  const [selectedSuggestion, setSelectedSuggestion] = useState('');
  const [openAI, setOpenAI] = useState(false);
  const [genAI, setGenAI] = useState(false);
  const [isPromptRetried, setIsPromptRetried] = useState(false);
  const [isPromptGenerating, setIsPromptGenerating] = useState<boolean>(false);
  // Concatenated string to keeps track of the downloaded chat history
  const [downloadChatHistory, setDownloadChatHistory] = useState(`AI: ${INITIAL_CHATBOT_MESSAGE}\n`);
  const [promptRetriedSource, setPromptRetriedSource] = useState('');
  const [isUpdatingChatHistory, setIsUpdatingChatHistory] = useState<UpdatingChatHistoryState>({
    state: false,
    action: '',
  });
  const [deviceType, setDeviceType] = useState<string>('Laptop/Desktop');

  // Device type detection
  useEffect(() => {
    const isMobile = /iPhone|Android/i.test(navigator.userAgent);
    const detectedDeviceType = isMobile ? 'Mobile' : 'Laptop/Desktop';
    setDeviceType(detectedDeviceType);
  }, []);

  // Use effect for resetting the app
  useEffect(() => {
    if (resetApp) {
      setResetApp(false);
    }
  }, [resetApp]);

  return (
    <SessionContext.Provider
      value={{
        pageView,
        setPageView,
        chatSessionId,
        setChatSessionId,
        accountDetails,
        setAccountDetails,
        selectedSuggestion,
        setSelectedSuggestion,
        openAI,
        setOpenAI,
        genAI,
        setGenAI,
        isPromptRetried,
        setIsPromptRetried,
        isPromptGenerating,
        setIsPromptGenerating,
        downloadChatHistory,
        setDownloadChatHistory,
        promptRetriedSource,
        setPromptRetriedSource,
        isUpdatingChatHistory,
        setIsUpdatingChatHistory,
        deviceType,
      }}
    >
      {props.children}
    </SessionContext.Provider>
  );
};
